/**
 * History layout with additional deposits sidebar
 */
import {
    Help as HelpIcon,
    Logout,
    Menu as MenuIcon,
    Person,
    Settings,
    Terrain,
} from '@mui/icons-material';
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import { DEPOSITS_SIDEBAR_COLLAPSED_WIDTH, DEPOSITS_SIDEBAR_WIDTH, DepositsSidebar } from '@/components/History/DepositsSidebar';
import { FieldFormModal } from '@/components/Fields/FieldFormModal';
import { HistoryProvider, useHistory } from '@/contexts/HistoryContext';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { Sidebar, SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from './Sidebar';
import { useInstructionsGuideStore } from '@/store/instructionsGuideStore';

interface HistoryLayoutProps {
  children: React.ReactNode;
}

const HistoryFieldIdFromUrl: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { setSelectedDepositId } = useHistory();

  useEffect(() => {
    const fieldId = searchParams.get('fieldId');
    if (fieldId) {
      setSelectedDepositId(fieldId);
    }
  }, [searchParams, setSelectedDepositId]);

  return null;
};

const HistoryLayoutContent: React.FC<HistoryLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const {
    fieldFormOpen,
    fieldFormMode,
    fieldFormInitialValues,
    closeFieldForm,
    onFieldSaved,
  } = useHistory();

  const showHistory = Boolean(user);
  const showPlaceholder = !user;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [depositsSidebarCollapsed, setDepositsSidebarCollapsed] = useState(false);
  const openGuide = useInstructionsGuideStore((s) => s.openGuide);
  const [depositsDrawerOpen, setDepositsDrawerOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/auth');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogoClick = () => {
    navigate('/app/home');
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleDepositsSidebarToggle = () => {
    setDepositsSidebarCollapsed(!depositsSidebarCollapsed);
  };

  const getInitials = (firstName?: string | null, lastName?: string | null) => {
    const first = firstName?.charAt(0).toUpperCase() || '';
    const last = lastName?.charAt(0).toUpperCase() || '';
    return first + last || user?.email?.charAt(0).toUpperCase() || '?';
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <HistoryFieldIdFromUrl />
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 1,
        }}
      >
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {isMobile && showHistory && (
            <Tooltip title="Deposits">
              <IconButton
                color="inherit"
                aria-label="Select deposit"
                onClick={() => setDepositsDrawerOpen(true)}
                sx={{ mr: 1 }}
              >
                <Terrain />
              </IconButton>
            </Tooltip>
          )}

          {/* Logo/Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                fontWeight: 'bold',
                '&:hover': {
                  color: 'primary.dark',
                }
              }}
              onClick={handleLogoClick}
            >
              QuickField
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  '& .help-icon': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }
              }}
              onClick={() => openGuide()}
            >
              <IconButton
                size="small"
                className="help-icon"
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.dark',
                  }
                }}
                title="User Guide"
              >
                <HelpIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Help center
              </Typography>
            </Box>
          </Box>

          {/* User menu */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Admin button - for admin and organization admin users */}
              {((user.role === UserRole.ADMIN || user.role === 'admin') ||
                (user.role === UserRole.ORGANIZATION_ADMIN || user.role === 'organization_admin')) && (
                <IconButton
                  component={Link}
                  to={(user.role === UserRole.ADMIN || user.role === 'admin') ? "/admin" : "/organization-admin"}
                  size="large"
                  color="inherit"
                  title={(user.role === UserRole.ADMIN || user.role === 'admin') ? "Admin Panel" : "Organization Admin Panel"}
                  sx={{ mr: 1 }}
                >
                  <Settings />
                </IconButton>
              )}

              <Typography variant="body2" sx={{ mr: 2, display: { xs: 'none', sm: 'block' } }}>
                {user.full_name || user.email}
              </Typography>

              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {getInitials(user.first_name, user.last_name)}
                </Avatar>
              </IconButton>

              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={open}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { minWidth: 200 }
                }}
              >
                <MenuItem onClick={handleProfileClick}>
                  <ListItemIcon>
                    <Person fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Profile</ListItemText>
                </MenuItem>

                <Divider />

                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Main Navigation Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { md: sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH },
          flexShrink: { md: 0 },
          transition: 'width 0.3s ease',
          mt: 8, // Account for AppBar height
        }}
      >
        {isMobile ? (
          <Sidebar
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
          />
        ) : (
          <Sidebar
            variant="permanent"
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
          />
        )}
      </Box>

      {/* Deposits Sidebar - desktop: permanent; mobile: temporary drawer from toolbar */}
      {showHistory && (
        isMobile ? (
          <DepositsSidebar
            variant="temporary"
            open={depositsDrawerOpen}
            onClose={() => setDepositsDrawerOpen(false)}
            collapsed={false}
          />
        ) : (
          <Box
            component="aside"
            sx={{
              width: depositsSidebarCollapsed ? DEPOSITS_SIDEBAR_COLLAPSED_WIDTH : DEPOSITS_SIDEBAR_WIDTH,
              flexShrink: 0,
              mt: 8, // Account for AppBar height
              transition: 'width 0.3s ease',
            }}
          >
            <DepositsSidebar
              collapsed={depositsSidebarCollapsed}
              onToggleCollapse={handleDepositsSidebarToggle}
              mainSidebarCollapsed={sidebarCollapsed}
            />
          </Box>
        )
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          ml: {
            md: `${SIDEBAR_WIDTH - 180}px`
          },
          mt: 8, // Account for AppBar height
          transition: 'margin 0.3s ease',
          height: 'calc(100vh - 64px)', // Full height minus AppBar
          overflow: 'auto', // Enable scrolling for main content
          minWidth: 0, // Allow content to shrink
          boxSizing: 'border-box',
        }}
      >
        {showPlaceholder ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100%', gap: 1 }}>
            <Typography variant="h5" fontWeight={600}>History</Typography>
            <Typography variant="h6" color="text.secondary">Available Soon</Typography>
            <Typography variant="body1" color="text.secondary">History page is not available yet. Please wait for updates.</Typography>
          </Box>
        ) : (
          children
        )}
      </Box>

      <FieldFormModal
        open={fieldFormOpen}
        onClose={closeFieldForm}
        mode={fieldFormMode}
        initialValues={fieldFormInitialValues}
        onSaved={onFieldSaved}
      />
    </Box>
  );
};

export const HistoryLayout: React.FC<HistoryLayoutProps> = ({ children }) => {
  return (
    <HistoryProvider>
      <HistoryLayoutContent>{children}</HistoryLayoutContent>
    </HistoryProvider>
  );
};
